"use client";

import React from "react";
import { Calendar, User, ArrowLeft } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";



function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="h-64 rounded-xl bg-muted/20 animate-pulse" />
        ))}
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="overflow-hidden flex flex-col border-border/50 bg-card/50 backdrop-blur-md shadow-md hover:shadow-primary/20 transition-all duration-300 group">
          {post.featuredImage ? (
            <AspectRatio ratio={16 / 9}>
              <img
                src={post.featuredImage}
                alt={post.title}
                className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-500"
              />
            </AspectRatio>
          ) : (
            <AspectRatio ratio={16 / 9} className="bg-muted flex items-center justify-center">
              <span className="text-muted-foreground text-xs font-default uppercase tracking-widest">No Visual</span>
            </AspectRatio>
          )}
          <CardContent className="p-5 flex-1 flex flex-col">
            <h3 className="font-bold font-default text-lg mb-3 line-clamp-2 text-foreground group-hover:text-primary transition-colors">
              {post.title}
            </h3>
            <div className="mt-auto pt-4">
              <Link to={post.link || "#"}>
                <Button variant="outline" size="sm" className="w-full border-primary/50 hover:bg-primary hover:text-primary-foreground transition-all">
                  Read Update
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function RelatedPostsSection() {
  
  const wp_query = new WP_Query({ post_type: "post", per_page: 3 });

  return (
    <div className="mt-16">
      <h2 className="text-2xl md:text-3xl font-bold font-default mb-8 text-foreground tracking-tight">
        Latest from the <span className="text-secondary">Horizon</span>
      </h2>
      <PostsProvider wp_query={wp_query}>
        <RelatedPostsConsumer />
      </PostsProvider>
    </div>
  );
}



function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto px-4 max-w-4xl py-16 animate-pulse">
        <div className="h-12 bg-muted/20 rounded-md w-3/4 mb-6" />
        <div className="flex gap-4 mb-8">
          <div className="w-12 h-12 bg-muted/20 rounded-lg" />
          <div className="space-y-2">
            <div className="h-4 bg-muted/20 rounded w-32" />
            <div className="h-4 bg-muted/20 rounded w-24" />
          </div>
        </div>
        <div className="h-96 bg-muted/20 rounded-xl mb-10" />
        <div className="space-y-4">
          <div className="h-4 bg-muted/20 rounded w-full" />
          <div className="h-4 bg-muted/20 rounded w-full" />
          <div className="h-4 bg-muted/20 rounded w-5/6" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 max-w-4xl py-16 text-center">
        <p className="text-destructive mb-4 font-default">Failed to load the transmission.</p>
        <Link to="/HomePage">
          <Button variant="outline" className="border-primary/50">Return to Base</Button>
        </Link>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto px-4 max-w-4xl py-16 text-center">
        <h1 className="text-3xl font-bold font-default mb-4 text-foreground">Signal Lost</h1>
        <p className="text-muted-foreground mb-8 font-default">The article you are looking for has drifted beyond the horizon.</p>
        <Link to="/HomePage">
          <Button className="bg-primary text-primary-foreground shadow-lg shadow-primary/20">Browse All News</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="container mx-auto px-4 max-w-4xl py-12 md:py-20">
      <div className="mb-8">
        <Link to="/HomePage" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary transition-colors mb-6 font-default uppercase tracking-widest">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Newsfeed
        </Link>
        
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-default leading-tight mb-6 text-foreground bg-gradient-to-r from-white to-primary bg-clip-text text-transparent">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
          <div className="flex items-center gap-3">
            <Avatar className="w-10 h-10 border border-primary/30 shadow-sm shadow-primary/10">
              <AvatarImage src={post.author?.avatar?.["48"]} alt={post.author?.name} />
              <AvatarFallback className="bg-accent/20 text-accent">
                <User className="w-5 h-5" />
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-bold font-default text-foreground uppercase tracking-wider">{post.author?.name || "HH Records"}</p>
              <p className="text-xs font-default text-secondary">Label Insider</p>
            </div>
          </div>
          
          {post.date && (
            <div className="flex items-center gap-2 font-default uppercase tracking-tighter">
              <Calendar className="w-4 h-4 text-primary" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </time>
            </div>
          )}
        </div>
      </div>

      {post.featuredImage && (
        <div className="mb-12 rounded-2xl overflow-hidden shadow-2xl shadow-primary/10 border border-white/5">
          <AspectRatio ratio={21 / 9}>
            <img
              src={post.featuredImage}
              alt={post.title}
              className="object-cover w-full h-full"
            />
          </AspectRatio>
        </div>
      )}

      <div 
        className="prose prose-lg dark:prose-invert max-w-none text-foreground/80 prose-headings:font-default prose-headings:font-bold prose-headings:text-foreground prose-headings:tracking-tight prose-a:text-secondary hover:prose-a:text-secondary/80 prose-img:rounded-2xl prose-strong:text-primary"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      <Separator className="my-12 md:my-16 bg-border/30" />

      {}
      <div className="bg-card/40 backdrop-blur-xl rounded-2xl p-6 md:p-8 flex flex-col md:flex-row gap-6 items-start border border-white/5 shadow-xl">
        <Avatar className="w-20 h-20 border-2 border-primary/20 shadow-lg shadow-primary/10 shrink-0">
          <AvatarImage src={post.author?.avatar?.["96"]} alt={post.author?.name} />
          <AvatarFallback className="bg-secondary/20 text-secondary text-2xl font-default">
            {post.author?.name?.charAt(0) || "H"}
          </AvatarFallback>
        </Avatar>
        <div>
          <h3 className="text-xl font-bold font-default mb-2 text-foreground tracking-wide">
            Curated by {post.author?.name || "Heavens Horizons Team"}
          </h3>
          <p className="text-muted-foreground leading-relaxed mb-4 font-default text-sm">
            {post.author?.name 
              ? `A key voice at Heavens Horizons Records, bringing you the latest on EDM, Country Pop, and the future of independent music culture.` 
              : `The Heavens Horizons Editorial Team covers the intersection of electronic music, festival culture, and the rising stars of the Michigan independent scene.`}
          </p>
          {post.author?.link && (
            <Link to={post.author.link}>
              <Button variant="outline" size="sm" className="border-secondary/50 text-secondary hover:bg-secondary hover:text-secondary-foreground">View Artist Profile</Button>
            </Link>
          )}
        </div>
      </div>

      <Separator className="my-12 md:my-16 bg-border/30" />

      <RelatedPostsSection />
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof wvcClient !== 'undefined' ? wvcClient.getCurrentWPQueryParams() : { post_type: "post", per_page: 1 };
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="234028" id="single-post-template-section" className="bg-background text-foreground min-h-screen selection:bg-primary/30">
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}